import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Button from './ui/Button';
import FeatureGate from './FeatureGate';
import { downloadVirtualTerminalPoster } from '../utils/downloadVirtualTerminalPoster';

/**
 * VirtualAccountCard Component
 *
 * Small square box for displaying the merchant's Paystack Terminal details.
 * Designed to be embedded inside Sales Channel card.
 *
 * Feature gate remains payments.dva while the rollout migrates from DVA copy
 * to the Paystack Virtual Terminal rail.
 *
 * IMPORTANT: This component uses Redux store data directly to ensure consistency
 * with the Settings page. Previously it used /api/dva/my-account which could
 * return wrong merchant data due to findFirst query issues.
 */

// Copy icon component
const CopyIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

// Check icon for copied state
const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const DownloadIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

/**
 * Compact payment rail box - Small square with green dotted border
 * For embedding in Sales Channel card
 */
const CompactDVABox = ({ dva, onCopy, copiedField, onDownloadPoster, posterDownloading }) => {
  if (!dva?.virtualAccount && !dva?.virtualTerminal) return null;

  const rail = dva.virtualTerminal || dva.virtualAccount;
  const isTerminal = !!dva.virtualTerminal;
  const { bankName, accountNumber, accountName, code, name, destinationStatus } = rail;

  // Row with copy button
  const CopyRow = ({ label, value, field }) => {
    if (!value) return null;

    return (
      <div
        onClick={() => onCopy(value, field)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 0',
          cursor: 'pointer'
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '1px' }}>{label}</div>
          <div style={{
            fontSize: field === 'accountNumber' ? '13px' : '11px',
            fontWeight: field === 'accountNumber' ? '600' : '500',
            color: '#111827',
            fontFamily: field === 'accountNumber' ? 'monospace' : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {value}
          </div>
        </div>
        <div style={{
          marginLeft: '6px',
          color: copiedField === field ? '#10B981' : '#9CA3AF',
          transition: 'color 0.2s'
        }}>
          {copiedField === field ? <CheckIcon /> : <CopyIcon />}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      border: '2px dashed #10B981',
      borderRadius: '12px',
      padding: '12px',
      background: '#fff',
      width: '160px',
      minWidth: '160px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #E5E7EB'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#10B981' }}>{isTerminal ? 'Paystack Terminal' : 'Legacy Bank Account'}</span>
      </div>

      {/* Account Details */}
      {isTerminal && <CopyRow label="Code" value={code} field="terminalCode" />}
      {accountNumber ? (
        <>
          <CopyRow label="Bank" value={bankName || 'Paystack'} field="bankName" />
          <CopyRow label="Acct No." value={accountNumber} field="accountNumber" />
          <CopyRow label="Name" value={accountName || name} field="accountName" />
          {isTerminal && (
            <button
              type="button"
              onClick={onDownloadPoster}
              disabled={posterDownloading}
              title="Download terminal banner"
              style={{
                marginTop: '8px',
                width: '100%',
                minHeight: '28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                border: '1px solid rgba(13, 116, 99, 0.22)',
                borderRadius: '8px',
                background: '#e8f6f1',
                color: '#0d7463',
                fontSize: '10px',
                fontWeight: 700,
                cursor: posterDownloading ? 'wait' : 'pointer',
                opacity: posterDownloading ? 0.7 : 1
              }}
            >
              <DownloadIcon />
              {posterDownloading ? 'Preparing...' : 'Banner'}
            </button>
          )}
        </>
      ) : (
        <CopyRow label="Alerts" value={destinationStatus || 'Pending'} field="destinationStatus" />
      )}
    </div>
  );
};

const VirtualAccountDisplay = ({ dva, onCopy, copiedField, onProvision, provisioning, onDownloadPoster, posterDownloading }) => {
  if (!dva) {
    return null;
  }

  // If no virtual account yet, show compact provisioning box
  if (!dva.hasVirtualAccount) {
    return (
      <div style={{
        border: '2px dashed #10B981',
        borderRadius: '12px',
        padding: '12px',
        background: '#fff',
        width: '160px',
        minWidth: '160px',
        textAlign: 'center'
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" style={{ margin: '0 auto 8px' }}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        <div style={{ fontSize: '11px', fontWeight: '600', color: '#10B981', marginBottom: '6px' }}>
          Paystack Terminal
        </div>
        <p style={{ fontSize: '10px', color: '#6B7280', margin: '0 0 10px 0', lineHeight: 1.4 }}>
          {dva.message || 'Activate your Paystack payment terminal'}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={onProvision}
          disabled={provisioning}
          style={{
            background: '#10B981',
            color: 'white',
            fontWeight: '500',
            fontSize: '10px',
            padding: '6px 10px',
            width: '100%',
            border: 'none'
          }}
        >
          {provisioning ? 'Setting up...' : 'Activate'}
        </Button>
      </div>
    );
  }

  // Show compact payment rail box
  return (
    <CompactDVABox
      dva={dva}
      onCopy={onCopy}
      copiedField={copiedField}
      onDownloadPoster={onDownloadPoster}
      posterDownloading={posterDownloading}
    />
  );
};

const VirtualAccountCard = () => {
  // Use Redux store data directly to ensure consistency with Settings page
  // This fixes the bug where /api/dva/my-account could return wrong merchant's data
  // The storeSlice stores the merchant's store under `current`, not `store` —
  // reading `state.store.store` was always undefined, so the dashboard kept
  // showing the "Activate" CTA even for provisioned merchants. Same fix for
  // the loading flag: the slice uses `status` ('loading' | 'succeeded' | ...).
  const store = useSelector((state) => state.store?.current);
  const storeLoading = useSelector((state) => state.store?.status === 'loading');

  const [copiedField, setCopiedField] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [posterDownloading, setPosterDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [localDva, setLocalDva] = useState(null);

  // Build payment rail data from Redux store. Prefer VT, keep DVA as legacy fallback.
  const dva = localDva || (store?.paystackVirtualTerminalCode ? {
    hasVirtualAccount: true,
    virtualTerminal: {
      code: store.paystackVirtualTerminalCode,
      name: store.virtualTerminalName || 'Paystack Terminal',
      accountNumber: store.virtualTerminalAccountNumber,
      bankName: store.virtualTerminalBank,
      accountName: store.virtualTerminalAccountName || store.name,
      destinationStatus: store.virtualTerminalDestinationStatus
    }
  } : store?.virtualAccountNumber ? {
    hasVirtualAccount: true,
    virtualAccount: {
      accountNumber: store.virtualAccountNumber,
      bankName: store.virtualAccountBank || 'Wema Bank',
      accountName: store.virtualAccountName || store.name
    }
  } : {
    hasVirtualAccount: false,
    message: 'Activate your Paystack payment terminal'
  });

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleProvision = async () => {
    try {
      setProvisioning(true);
      setError(null);
      const response = await fetch('/api/virtual-terminal/provision', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update local state with the new VT data
        if (data.virtualTerminal) {
          setLocalDva({
            hasVirtualAccount: true,
            virtualTerminal: {
              code: data.virtualTerminal.code,
              name: data.virtualTerminal.name || 'Paystack Terminal',
              accountNumber: data.virtualTerminal.accountNumber,
              bankName: data.virtualTerminal.bankName,
              accountName: data.virtualTerminal.accountName,
              destinationStatus: data.virtualTerminal.destinationStatus
            }
          });
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to provision Paystack Terminal');
      }
    } catch (err) {
      console.error('Error provisioning Paystack Terminal:', err);
      setError('Failed to provision Paystack Terminal');
    } finally {
      setProvisioning(false);
    }
  };

  const handleDownloadPoster = async () => {
    try {
      setPosterDownloading(true);
      setError(null);
      await downloadVirtualTerminalPoster();
    } catch (err) {
      console.error('Error downloading terminal poster:', err);
      setError(err.message || 'Failed to download terminal banner');
    } finally {
      setPosterDownloading(false);
    }
  };

  if (storeLoading) {
    return (
      <div style={{
        background: '#d1d5db',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        minWidth: '200px',
        maxWidth: '300px',
        animation: 'pulse 2s infinite'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)'
        }}>
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            background: 'rgba(0,0,0,0.1)'
          }} />
          <div style={{
            width: '80px',
            height: '14px',
            background: 'rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }} />
        </div>
        <div style={{
          width: '100%',
          height: '80px',
          background: 'rgba(0,0,0,0.05)',
          borderRadius: '8px'
        }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'var(--color-red-50)',
        border: '1px solid var(--color-red-200)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3)',
        minWidth: '200px',
        maxWidth: '300px'
      }}>
        <p style={{
          margin: 0,
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-red-700)'
        }}>
          {error}
        </p>
        <button
          onClick={() => setError(null)}
          style={{
            marginTop: '8px',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-red-600)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <VirtualAccountDisplay
      dva={dva}
      onCopy={handleCopy}
      copiedField={copiedField}
      onProvision={handleProvision}
      provisioning={provisioning}
      onDownloadPoster={handleDownloadPoster}
      posterDownloading={posterDownloading}
    />
  );
};

// Wrapped component with FeatureGate
const VirtualAccountCardWithGate = ({ showUpgradePrompt = true }) => {
  return (
    <FeatureGate
      featureId="payments.dva"
      showUpgradePrompt={showUpgradePrompt}
      upgradeMessage="Virtual Accounts are available on PRO plans and above."
      fallback={
        <div style={{
          border: '2px dashed #9CA3AF',
          borderRadius: '12px',
          padding: '12px',
          background: '#F9FAFB',
          width: '160px',
          minWidth: '160px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" style={{ margin: '0 auto 8px' }}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#9CA3AF', marginBottom: '4px' }}>
            Bank Account
          </div>
          <p style={{ margin: 0, fontSize: '10px', color: '#9CA3AF' }}>
            Upgrade to unlock
          </p>
        </div>
      }
    >
      <VirtualAccountCard />
    </FeatureGate>
  );
};

export default VirtualAccountCardWithGate;
export { VirtualAccountCard, VirtualAccountDisplay, CompactDVABox };
